import { DisabledPrompt, Link, useRoute, useUIState } from "@bcm-dev/bcms-vite";
import { CustomAccordionClose, CustomAccordionContent, useCustomAccordion } from 'components/custom-ui/CustomAccordion';
import { FormContainer, FormRowView } from 'components/shared';
import MobileTableAccordionView, { MobileTableAccordionDataItem, MobileTableAccordionToggle } from 'components/shared/views/MobileTableAccordionView';
import { TransferTransaction } from 'model';
import React from 'react';
import { SVGAsset } from 'res';
import "./styles.css";

/**
 * @function MobileScheduledTransfersTable
 * @param {ARRAY} transfers - Array of transfers that need to be shown
 * @return {React.Component}
 * @exported true
 * @description The main component for MobileScheduledTransfersTable
 * @example
 * <MobileScheduledTransfersTable transfers={ transfers } />
 */
export default function MobileScheduledTransfersTable(props) {
  let transfers = props.transfers ?? TransferTransaction.initAll().filter(transfer => transfer.status === "scheduled");
  transfers = transfers.sort((a,b)=>{
    if( a.date.diff() < b.date.diff())
     return -1;
    if( a.date.diff() > b.date.diff())
     return 1;
    return 0;
})
  const transferRows = transfers.map(transfer => {
    return <MobileScheduledTransfersItem key={`Mobile_Transfer_${ transfer.id }`} transfer={ transfer } />
  }, []);

  return (
    <div className="mobile-scheduled-transfers-table">
      { transferRows }
    </div>
  );
}

/**
 * @function MobileScheduledTransfersItem
 * @param {TransferTransaction} transfer - The transfer to be shown
 * @return {React.Component}
 * @exported true
 * @description The individual transfer item shown in the table
 * @example
 * <MobileScheduledTransfersItem transfer={ transfer } />
 */
export function MobileScheduledTransfersItem(props) {
  const navigate = useRoute();
  const transfer = props.transfer ?? TransferTransaction.firstInstanceOf();
  const { key, config } = useUIState(`Mobile_Accordion_${ transfer.id }`, { isActive: false });

  const onEditTransferClick = id => () => {
    navigate(`/transfers/0/${ id }`);
};

  return (
    <MobileTableAccordionView active={ config.isActive }>
      <MobileScheduledTransfersItemHeader transfer={ transfer } />

      <CustomAccordionContent>
        <div className="custom-accordion-content-container">
          <div className="flex-container custom-accordion-header">
            <p className="flex-width">TRANSFER DETAILS</p>

            <CustomAccordionClose className="set-width clickable">
              <SVGAsset id="CloseX" className="icon" />
            </CustomAccordionClose>
          </div>

          <FormContainer>
            <FormRowView text="Type" className="light-text">
              <p><b>Internal Transfer</b></p>
            </FormRowView>

            <FormRowView text="Delivered By" className="light-text">
              <p><b>{ transfer.onDate.format("m/d/Y") }</b></p>
            </FormRowView>

            <FormRowView text="Schedule" className="light-text">
              <p><b>Once on { transfer.onDate.format("m/d/Y") }</b></p>
            </FormRowView>

            <FormRowView text="Note" className="light-text">
              <p><b>{ transfer.note }</b></p>
            </FormRowView>

            <FormRowView className="light-text">
              <a className="clickable" onClick={ onEditTransferClick(transfer.id) }>
                <span data-point={ `Edit_Transfer_Link_${ transfer.id }` }>Edit Transfer</span>
              </a>
            </FormRowView>

            <FormRowView className="light-text">
              <DisabledPrompt>
                <Link>
                  <span data-point={ `Cancel_Transfer_Link_${ transfer.id }` }>Cancel Transfer</span>
                </Link>
              </DisabledPrompt>
            </FormRowView>
          </FormContainer>
        </div>
      </CustomAccordionContent>
    </MobileTableAccordionView>
  );
}

/**
 * @function MobileScheduledTransfersItemHeader
 * @param {TransferTransaction} transfer - The transfer to be shown
 * @return {React.Component}
 * @exported true
 * @description The transfer item header in the table
 * @note This is seperate from the item in-order to use the useCustomAccordion() hook
 * @example
 * <MobileScheduledTransfersItemHeader transfer={ transfer } />
 */
export function MobileScheduledTransfersItemHeader({ transfer }) {
  const { state } = useCustomAccordion();

  return (
    <MobileTableAccordionToggle icon={ state ? "DownChevronIcon" : "RightChevronIcon" } rightValue={ transfer.amount.format() } depositID={ transfer.id }>
      <MobileTableAccordionDataItem columnHeader="From:" data={ `${ transfer.from?.nickname } - ${ transfer.from?.number }` } />
      <MobileTableAccordionDataItem columnHeader="To:" data={ `${ transfer.to?.nickname } - ${ transfer.to?.number }` } />
      <MobileTableAccordionDataItem columnHeader="Frequency:" data={ transfer.frequency } />
      <MobileTableAccordionDataItem columnHeader="Next Transfer:" data={ transfer.onDate.format("m/d/Y") } />
    </MobileTableAccordionToggle>
  );
}